/**
 * Paragraph Style Extension
 *
 * Adds support for paragraph-level styling (indentation, margins, etc.)
 * so that content pasted from Word or inserted by the AI agent preserves
 * formatting. Essential for indented paragraphs and nested indentations.
 */

import { Extension } from "@tiptap/core";

const PARAGRAPH_STYLE_TYPES = ["paragraph", "heading", "bulletList", "orderedList"];

function parseStyleAttr(element: Element): Record<string, string> {
  const style = element.getAttribute("style");
  if (!style) return {};
  const result: Record<string, string> = {};
  for (const part of style.split(";")) {
    const [key, val] = part.split(":").map((s) => s?.trim());
    if (key && val) {
      const camelKey = key.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
      result[camelKey] = val;
    }
  }
  return result;
}

function parsePt(value: string): string | null {
  const m = String(value).match(/^([\d.]+)\s*pt$/i);
  return m ? `${parseFloat(m[1])}pt` : null;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    paragraphStyle: {
      indent: () => ReturnType;
      outdent: () => ReturnType;
      setLineHeight: (value: string) => ReturnType;
      unsetLineHeight: () => ReturnType;
    };
  }
}

const INDENT_STEP = 36; // 36pt = 0.5 inch

export const ParagraphStyleExtension = Extension.create({
  name: "paragraphStyle",

  addCommands() {
    return {
      indent:
        () =>
        ({ tr, state, dispatch }) => {
          const { from, to } = state.selection;
          state.doc.nodesBetween(from, to, (node, pos) => {
            if (node.type.name === "paragraph" || node.type.name === "heading") {
              const currentMargin = node.attrs.marginLeft
                ? parseFloat(node.attrs.marginLeft)
                : 0;
              tr.setNodeMarkup(pos, undefined, {
                ...node.attrs,
                marginLeft: `${currentMargin + INDENT_STEP}pt`,
              });
            }
          });
          if (dispatch) dispatch(tr);
          return true;
        },
      outdent:
        () =>
        ({ tr, state, dispatch }) => {
          const { from, to } = state.selection;
          state.doc.nodesBetween(from, to, (node, pos) => {
            if (node.type.name === "paragraph" || node.type.name === "heading") {
              const currentMargin = node.attrs.marginLeft
                ? parseFloat(node.attrs.marginLeft)
                : 0;
              const newMargin = Math.max(0, currentMargin - INDENT_STEP);
              tr.setNodeMarkup(pos, undefined, {
                ...node.attrs,
                marginLeft: newMargin > 0 ? `${newMargin}pt` : null,
              });
            }
          });
          if (dispatch) dispatch(tr);
          return true;
        },
      setLineHeight:
        (value: string) =>
        ({ tr, state, dispatch }) => {
          const { from, to } = state.selection;
          state.doc.nodesBetween(from, to, (node, pos) => {
            if (node.type.name === "paragraph" || node.type.name === "heading") {
              tr.setNodeMarkup(pos, undefined, {
                ...node.attrs,
                lineHeight: value,
              });
            }
          });
          if (dispatch) dispatch(tr);
          return true;
        },
      unsetLineHeight:
        () =>
        ({ tr, state, dispatch }) => {
          const { from, to } = state.selection;
          state.doc.nodesBetween(from, to, (node, pos) => {
            if (node.type.name === "paragraph" || node.type.name === "heading") {
              tr.setNodeMarkup(pos, undefined, {
                ...node.attrs,
                lineHeight: null,
              });
            }
          });
          if (dispatch) dispatch(tr);
          return true;
        },
    };
  },

  addKeyboardShortcuts() {
    return {
      Tab: () => {
        // Only indent if not in a list or table
        const { state } = this.editor;
        const { $from } = state.selection;
        for (let d = $from.depth; d > 0; d--) {
          const nodeName = $from.node(d).type.name;
          if (
            nodeName === "listItem" ||
            nodeName === "bulletList" ||
            nodeName === "orderedList" ||
            nodeName === "table" ||
            nodeName === "tableRow" ||
            nodeName === "tableCell" ||
            nodeName === "tableHeader"
          ) {
            return false; // Let default handler handle it
          }
        }
        return this.editor.commands.indent();
      },
      "Shift-Tab": () => {
        const { state } = this.editor;
        const { $from } = state.selection;
        for (let d = $from.depth; d > 0; d--) {
          const nodeName = $from.node(d).type.name;
          if (
            nodeName === "listItem" ||
            nodeName === "bulletList" ||
            nodeName === "orderedList" ||
            nodeName === "table" ||
            nodeName === "tableRow" ||
            nodeName === "tableCell" ||
            nodeName === "tableHeader"
          ) {
            return false;
          }
        }
        return this.editor.commands.outdent();
      },
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: PARAGRAPH_STYLE_TYPES,
        attributes: {
          marginLeft: {
            default: null,
            parseHTML: (element) => {
              const styles = parseStyleAttr(element);
              const fromStyle = parsePt(styles.marginLeft ?? "");
              if (fromStyle) return fromStyle;
              const fromData = element.getAttribute("data-margin-left");
              if (fromData) return fromData;
              // Support Quill's ql-indent-X classes (e.g. from legacy agents or HTML imports)
              const qlIndent = element.className?.match(/\bql-indent-(\d+)\b/);
              if (qlIndent) {
                const level = parseInt(qlIndent[1], 10);
                return `${level * 24}pt`; // 24pt per level, matches outline-level styling
              }
              return null;
            },
            renderHTML: (attrs) =>
              attrs.marginLeft ? { style: `margin-left: ${attrs.marginLeft}` } : {},
          },
          marginRight: {
            default: null,
            parseHTML: (element) => {
              const styles = parseStyleAttr(element);
              return parsePt(styles.marginRight ?? "") || element.getAttribute("data-margin-right");
            },
            renderHTML: (attrs) =>
              attrs.marginRight ? { style: `margin-right: ${attrs.marginRight}` } : {},
          },
          marginTop: {
            default: null,
            parseHTML: (element) => {
              const styles = parseStyleAttr(element);
              return parsePt(styles.marginTop ?? "") || element.getAttribute("data-margin-top");
            },
            renderHTML: (attrs) =>
              attrs.marginTop ? { style: `margin-top: ${attrs.marginTop}` } : {},
          },
          marginBottom: {
            default: null,
            parseHTML: (element) => {
              const styles = parseStyleAttr(element);
              return parsePt(styles.marginBottom ?? "") || element.getAttribute("data-margin-bottom");
            },
            renderHTML: (attrs) =>
              attrs.marginBottom ? { style: `margin-bottom: ${attrs.marginBottom}` } : {},
          },
          textIndent: {
            default: null,
            parseHTML: (element) => {
              const styles = parseStyleAttr(element);
              return parsePt(styles.textIndent ?? "") || element.getAttribute("data-text-indent");
            },
            renderHTML: (attrs) =>
              attrs.textIndent ? { style: `text-indent: ${attrs.textIndent}` } : {},
          },
          lineHeight: {
            default: null,
            parseHTML: (element) => {
              const styles = parseStyleAttr(element);
              return styles.lineHeight || element.getAttribute("data-line-height");
            },
            renderHTML: (attrs) =>
              attrs.lineHeight ? { style: `line-height: ${attrs.lineHeight}` } : {},
          },
        },
      },
    ];
  },
});

export default ParagraphStyleExtension;
